import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  FaEnvelope, FaPhone, FaMapMarkerAlt, FaLinkedin, 
  FaGithub, FaTwitter, FaPaperPlane, FaCheck, FaExclamationTriangle
} from 'react-icons/fa';
import { useLanguage } from '../utils/LanguageContext';
import './AllComponents.css';

const Contact = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });

  const { t } = useLanguage();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setError(result.message || 'Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <FaEnvelope />,
      label: t('contact.email'),
      value: "a7krah@gmail.com",
      link: "mailto:a7krah@gmail.com"
    },
    {
      icon: <FaPhone />,
      label: t('contact.phone'),
      value: "+49 176 458 27800",
      link: "tel:+4917645827800"
    },
    {
      icon: <FaMapMarkerAlt />,
      label: t('contact.location'),
      value: t('about.locationValue'),
      link: "https://maps.google.com/?q=Düsseldorf,Germany"
    }
  ];

  const socialLinks = [
    {
      icon: <FaLinkedin />,
      name: "LinkedIn",
      url: "https://www.linkedin.com/in/aasakra/",
      color: "#0077b5"
    },
    {
      icon: <FaGithub />,
      name: "GitHub", 
      url: "https://github.com/ahmadasakra",
      color: "#333"
    },
    {
      icon: <FaTwitter />,
      name: "Twitter",
      url: "http://x.com/ahmedasakrah",
      color: "#1da1f2"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section id="contact" className="section contact-section">
      <div className="container">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.div variants={itemVariants} className="section-header text-center">
            <h2 className="gradient-text">{t('contact.title')}</h2>
            <p className="section-subtitle">
              {t('contact.subtitle')}
            </p>
          </motion.div>

          <div className="contact-content">
            <motion.div variants={itemVariants} className="contact-info">
              <div className="card">
                <h3>{t('contact.info')}</h3>
                <p>{t('contact.infoDescription')}</p>
                
                <div className="contact-details">
                  {contactInfo.map((info, index) => (
                    <a 
                      key={index}
                      href={info.link}
                      className="contact-item"
                      target={info.link.startsWith('http') ? '_blank' : '_self'}
                      rel={info.link.startsWith('http') ? 'noopener noreferrer' : ''}
                    >
                      <div className="contact-icon">
                        {info.icon}
                      </div>
                      <div className="contact-text">
                        <span className="contact-label">{info.label}</span>
                        <span className="contact-value">{info.value}</span>
                      </div>
                    </a>
                  ))}
                </div>

                <div className="social-links">
                  <h4>{t('contact.followMe')}</h4>
                  <div className="social-icons">
                    {socialLinks.map((social, index) => (
                      <motion.a
                        key={index}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-link"
                        style={{ '--social-color': social.color }}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {social.icon}
                        <span>{social.name}</span>
                      </motion.a>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="contact-form">
              <div className="card">
                <h3>{t('contact.sendMessage')}</h3>
                {isSubmitted ? (
                  <motion.div 
                    className="success-message"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                  >
                    <FaCheck />
                    <h4>{t('contact.messageSent')}</h4>
                    <p>{t('contact.thankYou')}</p>
                    <button 
                      onClick={() => setIsSubmitted(false)}
                      className="btn btn-secondary"
                      style={{ marginTop: '10px' }}
                    >
                      {t('contact.sendAnother')}
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    {error && (
                      <motion.div 
                        className="error-message"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        style={{
                          padding: '12px',
                          background: 'rgba(220, 38, 38, 0.1)',
                          borderRadius: '8px',
                          marginBottom: '16px',
                          fontSize: '14px',
                          color: '#dc2626',
                          textAlign: 'center',
                          border: '1px solid rgba(220, 38, 38, 0.2)'
                        }}
                      >
                        <FaExclamationTriangle style={{ marginRight: '8px' }} />
                        {error}
                      </motion.div>
                    )}
                    
                    <div className="form-group">
                      <input
                        type="text"
                        name="name"
                        placeholder={t('contact.placeholders.name')}
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="form-group">
                      <input
                        type="email"
                        name="email"
                        placeholder={t('contact.placeholders.email')}
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="form-group">
                      <input
                        type="text"
                        name="subject"
                        placeholder={t('contact.placeholders.subject')}
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="form-group">
                      <textarea
                        name="message"
                        placeholder={t('contact.placeholders.message')}
                        rows="5"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        disabled={isSubmitting}
                      ></textarea>
                    </div>
                    
                    <button 
                      type="submit" 
                      className="btn btn-primary submit-btn"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <span>{t('contact.sending')}</span>
                      ) : (
                        <>
                          <FaPaperPlane />
                          {t('contact.sendButton')}
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
      
      <div className="footer">
        <div className="container">
          <p>{t('footer.copyright')}</p>
        </div>
      </div>
    </section>
  );
};

export default Contact; 